import { useState } from 'react';
import { Box, Button, Card, Chip, Stack, Typography } from '@mui/material';
import { useTranslate } from 'react-admin';
import { useCanWrite } from '@/access/useCanWrite';
import type { AdminTenant } from '@/api/models';
import { StatusChip } from '@/components/shell/StatusChip';
import { tenantStatusTone } from './domain';
import { TenantEditNameDialog } from './TenantEditNameDialog';
import { TenantStatusDialog } from './TenantStatusDialog';
import { TenantTagsDialog } from './TenantTagsDialog';

interface TenantGeneralPanelProps {
  tenant: AdminTenant;
  onChanged: (tenant: AdminTenant) => void;
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'right' }}>
        {value}
      </Typography>
    </Stack>
  );
}

/**
 * General tenant data plus the two write operations the contract keeps
 * separate from the general `PATCH`: status (`PUT .../status`) and tags
 * (`PUT .../tags`). Every write action is hidden for `platform:auditor` via
 * `useCanWrite` — a UI convenience; the dialogs themselves still fall back
 * to the designed access-denied state on a `403 access_denied` from the API.
 * `tenant.tags` is the tenant's actual saved tag set (A3 contract addition —
 * `AdminTenant` now carries `tags`), so the tags dialog always prefills from
 * real data rather than only what the current session happened to set.
 */
export function TenantGeneralPanel({ tenant, onChanged }: TenantGeneralPanelProps) {
  const translate = useTranslate();
  const canWrite = useCanWrite();
  const [editNameOpen, setEditNameOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [tagsOpen, setTagsOpen] = useState(false);

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
      <Card sx={{ p: 2.5 }}>
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {translate('pay.tenants.detail.fieldStatus')}
            </Typography>
            <StatusChip label={translate(`pay.tenants.status.${tenant.status}`)} tone={tenantStatusTone(tenant.status)} />
          </Stack>
          <Field
            label={translate('pay.tenants.detail.fieldCreatedAt')}
            value={new Date(tenant.createdAt).toLocaleString('es-ES')}
          />
          {canWrite && (
            <Stack direction="row" spacing={1.5} sx={{ pt: 1 }}>
              <Button size="small" variant="outlined" onClick={() => setEditNameOpen(true)}>
                {translate('pay.tenants.detail.editNameAction')}
              </Button>
              <Button size="small" variant="outlined" onClick={() => setStatusOpen(true)}>
                {translate('pay.tenants.detail.changeStatusAction')}
              </Button>
            </Stack>
          )}
        </Stack>
      </Card>

      <Card sx={{ p: 2.5 }}>
        <Stack spacing={1.5}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {translate('pay.tenants.tags.title')}
          </Typography>
          {tenant.tags.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {translate('pay.tenants.tags.empty')}
            </Typography>
          ) : (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {tenant.tags.map((tag) => (
                <Chip key={`${tag.key}:${tag.value}`} label={`${tag.key}: ${tag.value}`} size="small" />
              ))}
            </Stack>
          )}
          {canWrite && (
            <Button size="small" variant="outlined" onClick={() => setTagsOpen(true)} sx={{ alignSelf: 'flex-start' }}>
              {translate('pay.tenants.tags.editAction')}
            </Button>
          )}
        </Stack>
      </Card>

      <TenantEditNameDialog
        open={editNameOpen}
        tenant={tenant}
        onClose={() => setEditNameOpen(false)}
        onSaved={(updated) => {
          setEditNameOpen(false);
          onChanged(updated);
        }}
      />
      <TenantStatusDialog
        open={statusOpen}
        tenant={tenant}
        onClose={() => setStatusOpen(false)}
        onSaved={(updated) => {
          setStatusOpen(false);
          onChanged(updated);
        }}
      />
      <TenantTagsDialog
        open={tagsOpen}
        tenantCode={tenant.code}
        initialTags={tenant.tags}
        onClose={() => setTagsOpen(false)}
        onSaved={(tags) => {
          setTagsOpen(false);
          onChanged({ ...tenant, tags });
        }}
      />
    </Box>
  );
}
